import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { alertRulesService } from "@/services/alert-rules.service";

// Fluxo de inscrição push (Fase 3, motor de alertas real). Dois canais
// por baixo, escolhidos em runtime por Capacitor.isNativePlatform():
//   - app Android nativo (Capacitor, 2026-09-09): FCM via
//     ensureNativeFcmSubscription — Web Push não roda em segundo plano
//     dentro do WebView do Capacitor (sem processo de navegador por trás
//     pra acordar o Service Worker).
//   - navegador/PWA instalado (Web Push padrão): Android sem restrição,
//     iPhone só se o site estiver "adicionado à Tela de Início" (Safari
//     16.4+).
//
// ensurePushSubscription() continua disparado quando o operador liga o
// canal "Notificação push no celular" pela primeira vez (ver
// CreateAlertDialog em routes/index.tsx) — cobre o caso Web Push (o
// AlertEngine entrega pra TODAS as inscrições da empresa, não só de quem
// ligou o toggle, mas o toggle é o ÚNICO gatilho de registro no navegador,
// já que pedir permissão de notificação sem contexto é mal visto lá).
//
// ensureNativeFcmSubscription() TAMBÉM é chamado direto por
// useNativePushRegistration (routes/__root.tsx), uma vez por sessão,
// assim que o usuário loga dentro do app nativo — nunca esperar o
// operador achar e ligar o toggle de um alerta específico pra registrar o
// token do aparelho: um token FCM é por DISPOSITIVO/usuário, não por
// regra, e pedir permissão nativa de notificação logo no login é UX
// padrão em app mobile (raiz do bug 2026-09-09: regra de ignição já tinha
// channelPush=true desde o Web Push de agosto, então o toggle nunca
// disparava de novo dentro do app nativo — token FCM nunca chegava a ser
// criado, silêncio total apesar do backend/som estarem 100% configurados).
export class PushUnsupportedError extends Error {
  constructor() {
    super("Este navegador não suporta notificações push.");
  }
}
export class PushPermissionDeniedError extends Error {
  constructor() {
    super("Permissão de notificação negada.");
  }
}

// VAPID vem em base64url (RFC 4648 §5) — PushManager.subscribe exige um
// Uint8Array bruto, não a string.
function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function ensureNativeFcmSubscription(): Promise<void> {
  const current = await PushNotifications.checkPermissions();
  let status = current.receive;
  if (status === "prompt" || status === "prompt-with-rationale") {
    status = (await PushNotifications.requestPermissions()).receive;
  }
  if (status !== "granted") {
    throw new PushPermissionDeniedError();
  }

  await new Promise<void>((resolve, reject) => {
    // Só um "registration"/"registrationError" chega por chamada de
    // .register() — remove os dois listeners assim que um deles resolver,
    // pra não vazar handler numa próxima tentativa (usuário desliga/liga
    // o toggle de novo).
    void PushNotifications.addListener("registration", (token) => {
      void PushNotifications.removeAllListeners();
      alertRulesService.subscribeFcm({ token: token.value }).then(resolve, reject);
    });
    void PushNotifications.addListener("registrationError", (err) => {
      void PushNotifications.removeAllListeners();
      reject(new Error(err.error || "Falha ao registrar push nativo"));
    });
    void PushNotifications.register();
  });
}

export async function ensurePushSubscription(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    return ensureNativeFcmSubscription();
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new PushUnsupportedError();
  }

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new PushPermissionDeniedError();
  }

  const existing = await registration.pushManager.getSubscription();
  const { publicKey } = await alertRulesService.getVapidPublicKey();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    }));

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("Inscrição push inválida");
  }

  await alertRulesService.subscribePush({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent,
  });
}
